// Action-required card for a flagged email. Two CTAs: "Reply yourself"
// opens Gmail in a new tab pointed at the thread; "Let Jarvis draft" triggers
// a redraft from scratch via the existing redraft endpoint (override flag).

import { ExternalLink } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import type { EmailDraftListItem } from '@/types'

interface Props {
  item: EmailDraftListItem
  onSkip: () => void
  skipping: boolean
}

function gmailThreadUrl(threadId: string): string {
  // Gmail web UI opens individual threads via /mail/u/0/#inbox/<threadId>.
  return `https://mail.google.com/mail/u/0/#inbox/${threadId}`
}

function typeBadge(type: string): { label: string; color: string } {
  switch (type) {
    case 'lead':
      return { label: 'New Lead · HIGH', color: 'text-jarvis-red border-jarvis-red/40 bg-jarvis-red/10' }
    case 'financial':
      return { label: 'Financial', color: 'text-jarvis-amber border-jarvis-amber/40 bg-jarvis-amber/10' }
    case 'client':
      return { label: 'Client', color: 'text-jarvis-blue border-jarvis-blue/40 bg-jarvis-blue/10' }
    case 'vendor':
      return { label: 'Vendor', color: 'text-jarvis-muted border-jarvis-border bg-jarvis-surface-2' }
    default:
      return { label: 'Review', color: 'text-jarvis-muted border-jarvis-border bg-jarvis-surface-2' }
  }
}

export function EmailFlagCard({ item, onSkip, skipping }: Props) {
  const badge = typeBadge(item.emailType)
  return (
    <JarvisCard>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-base">🚩</span>
          <span
            className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded border ${badge.color}`}
          >
            {badge.label}
          </span>
        </div>
        <button
          onClick={onSkip}
          disabled={skipping}
          className="text-xs uppercase tracking-wider text-jarvis-muted hover:text-jarvis-text transition-colors disabled:opacity-50"
        >
          {skipping ? 'Dismissing…' : 'Dismiss'}
        </button>
      </div>

      <div className="text-sm font-medium text-jarvis-text mb-1">
        {item.from ?? '(unknown sender)'}
      </div>
      <div className="text-sm text-jarvis-muted mb-3 truncate">
        {item.subject ?? '(no subject)'}
      </div>

      {item.flagReason && (
        <p className="text-sm text-jarvis-text mb-4 border-l-2 border-jarvis-blue/40 pl-3">
          {item.flagReason}
        </p>
      )}

      <div className="flex gap-2">
        <a
          href={gmailThreadUrl(item.gmailThreadId)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 bg-jarvis-blue hover:bg-jarvis-blue-bright px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
        >
          Open in Gmail <ExternalLink size={14} />
        </a>
      </div>
    </JarvisCard>
  )
}
